import { Box } from "@mui/material";
import React, { DependencyList, useCallback, useEffect, useLayoutEffect, useState } from "react";

interface IProps {
    containerElementId: string;
    deps?: DependencyList;
}

/**
 * A container component that resizes relative to the width of its parent element, provided by @param containerElementId.
 * Useful when static, numeric values are required for width - e.g. for overflow.
 *
 * @param deps Additional dependency that cause a resize.
 */
export const ContentResizingContainer: React.FC<React.PropsWithChildren<IProps>> = ({
    containerElementId,
    deps = [],
    children,
}) => {
    const [containerWidth, setContainerWidth] = useState<number>();

    const getContainerWidth = useCallback(
        (): number | undefined => document.querySelector(`#${containerElementId}`)?.clientWidth,
        [containerElementId],
    );

    useLayoutEffect(
        () => {
            setContainerWidth(getContainerWidth());
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getContainerWidth, ...deps],
    );

    useEffect(() => {
        // Intentionally omitting debounce because it feels smoother without, and we assume users are not resizing often.
        const handleResize = (): void => {
            setContainerWidth(getContainerWidth());
        };

        // In theory, not ideal to listen to resize, but this shouldn't happen too often.
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [getContainerWidth]);

    return (
        <Box justifySelf="center" width="100%" height="100%" maxWidth={containerWidth}>
            {children}
        </Box>
    );
};
